import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { downloadInvoice, fetchInvoiceData, formatCurrency, formatDate } from '../utils/reportUtils';
import {
  PageHeader, Card, Badge, Btn, Modal, FormGroup, DataTable, Td, Tr,
  Avatar, SearchBar, FilterBar, FilterSelect, Paginator, ResultCount,
  ActiveFilters,
} from '../components/UI';

const CYLINDERS = ['14.2 kg (Regular)','5 kg (Small)','19 kg (Commercial)','47.5 kg (Commercial Large)'];
const PRICES = {'14.2 kg (Regular)':903,'5 kg (Small)':338,'19 kg (Commercial)':1346,'47.5 kg (Commercial Large)':2450};
const SC = {
  Delivered:          { type:'success', label:'Delivered' },
  Pending:            { type:'warning', label:'Pending' },
  'Out for Delivery': { type:'info',    label:'In Transit' },
  Cancelled:          { type:'danger',  label:'Cancelled' },
};
const STATUS_OPTS = [
  { value:'All',               label:'All Status' },
  { value:'Pending',           label:'Pending' },
  { value:'Out for Delivery',  label:'In Transit' },
  { value:'Delivered',         label:'Delivered' },
  { value:'Cancelled',         label:'Cancelled' },
];
const PER_PAGE = 20;

/* ── Invoice Preview Modal ───────────────────────────────── */
function InvoiceModal({ booking, onClose }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [dlLoading, setDlLoading] = useState(false);

  useEffect(() => {
    fetchInvoiceData(booking.id)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [booking.id]);

  const handleDownload = async () => {
    setDlLoading(true);
    try {
      await downloadInvoice(booking.id, booking.bookingNo);
      toast.success('Invoice downloaded!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDlLoading(false);
    }
  };

  // Normalise the API response shape
  const b  = data?.booking  || data?.invoice  || booking;
  const c  = data?.customer || {};
  const co = data?.company  || data?.biller   || {};
  const items  = data?.items || [];
  const totals = data?.total || {};
  const amount = parseFloat(b?.amount || totals.subtotal || booking.amount || 0);
  const gst    = parseFloat(totals.tax  || amount * 0.05);
  const total  = parseFloat(totals.total || amount + gst);

  return (
    <Modal
      title="Booking Invoice"
      subtitle={`${booking.bookingNo} · ${booking.customer?.name || ''}`}
      onClose={onClose}
      maxWidth={640}
    >
      {/* Action bar */}
      <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginBottom:16 }}>
        <Btn variant="secondary" onClick={onClose}>Close</Btn>
        <Btn
          variant="primary"
          icon="⬇"
          onClick={handleDownload}
          disabled={dlLoading}
        >
          {dlLoading ? 'Downloading…' : 'Download PDF'}
        </Btn>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign:'center', padding:'40px 0', color:'var(--text-muted)' }}>
          <div style={{ width:28, height:28, border:'2px solid var(--border-default)', borderTopColor:'var(--brand)', borderRadius:'50%', animation:'spin .7s linear infinite', margin:'0 auto 12px' }} />
          Loading invoice data…
        </div>
      )}

      {/* Error — still show download option */}
      {!loading && error && (
        <div>
          <div style={{
            padding:'14px 18px', borderRadius:'var(--radius-md)', marginBottom:16,
            background:'var(--accent-amber-dim)', border:'1px solid rgba(245,158,11,.25)',
            color:'var(--accent-amber)', fontSize:13,
          }}>
            ⚠ Preview unavailable: {error}
            <div style={{ fontSize:12, marginTop:4, color:'var(--text-muted)' }}>
              You can still download the PDF using the button above.
            </div>
          </div>

          {/* Fallback summary from local booking data */}
          <div style={{ background:'var(--bg-elevated)', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border-subtle)', background:'linear-gradient(135deg,rgba(249,115,22,.1),transparent)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em' }}>Booking Reference</div>
                  <div style={{ fontSize:20, fontWeight:800, color:'var(--brand)', fontFamily:'var(--font-mono)', marginTop:2 }}>#{booking.bookingNo}</div>
                </div>
                <Badge type={SC[booking.status]?.type || 'warning'} dot>{booking.status}</Badge>
              </div>
            </div>
            <div style={{ padding:'16px 20px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {[
                ['Customer',    booking.customer?.name || '—'],
                ['Connection',  booking.customer?.connectionNo || '—'],
                ['Cylinder',    booking.cylinderType],
                ['Booking Date',booking.bookingDate],
                ['Amount',      `₹${parseFloat(booking.amount).toLocaleString('en-IN')}`],
                ['Created by',  booking.creator?.name || '—'],
              ].map(([l,v]) => (
                <div key={l}>
                  <div style={{ fontSize:10.5, color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:3 }}>{l}</div>
                  <div style={{ fontSize:13, color:'var(--text-primary)', fontWeight:600 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Full invoice preview */}
      {!loading && !error && data && (
        <div style={{ background:'var(--bg-elevated)', border:'1px solid var(--border-default)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
          {/* Header */}
          <div style={{ background:'linear-gradient(135deg,rgba(249,115,22,.15),rgba(249,115,22,.03))', borderBottom:'1px solid var(--brand-glow)', padding:'18px 22px', display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:5 }}>
                <span style={{ fontSize:18 }}>🔥</span>
                <div>
                  <div style={{ fontSize:14, fontWeight:800, color:'var(--text-primary)' }}>{co.name || 'Venad Communications'}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>IndianOil Gas Distributor</div>
                </div>
              </div>
              <div style={{ fontSize:11, color:'var(--text-muted)', lineHeight:1.7 }}>
                {co.address || 'Thiruvananthapuram, Kerala'}<br />
                {co.gstin && <>GSTIN: {co.gstin}</>}
                {co.phone && <> · Ph: {co.phone}</>}
              </div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em' }}>Invoice</div>
              <div style={{ fontSize:20, fontWeight:800, color:'var(--brand)', fontFamily:'var(--font-mono)', marginTop:2 }}>
                #{b.bookingNo || b.invoiceNumber || b.id}
              </div>
              <Badge type={SC[b.status]?.type || 'warning'} dot>{b.status || 'Pending'}</Badge>
            </div>
          </div>

          {/* Bill-to + details */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', borderBottom:'1px solid var(--border-subtle)' }}>
            <div style={{ padding:'14px 20px', borderRight:'1px solid var(--border-subtle)' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>Bill To</div>
              <div style={{ fontSize:13, fontWeight:700, color:'var(--text-primary)', marginBottom:4 }}>{c.name || booking.customer?.name || '—'}</div>
              <div style={{ fontSize:11.5, color:'var(--text-secondary)', lineHeight:1.8 }}>
                {(c.connectionNo || booking.customer?.connectionNo) && <div style={{ fontFamily:'var(--font-mono)', color:'var(--accent-blue)' }}>{c.connectionNo || booking.customer?.connectionNo}</div>}
                {(c.phone || booking.customer?.phone) && <div>{c.phone || booking.customer?.phone}</div>}
                {(c.address || booking.customer?.address) && <div>{c.address || booking.customer?.address}</div>}
              </div>
            </div>
            <div style={{ padding:'14px 20px' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>Details</div>
              {[
                ['Date',      formatDate(b.bookingDate)],
                ['Delivery',  b.deliveryDate ? formatDate(b.deliveryDate) : 'Pending'],
                ['Cylinder',  b.cylinderType],
              ].map(([l,v]) => (
                <div key={l} style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:5 }}>
                  <span style={{ color:'var(--text-muted)' }}>{l}</span>
                  <span style={{ color:'var(--text-secondary)', fontWeight:500 }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Line items */}
          <div style={{ padding:'14px 20px' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
              <thead>
                <tr>
                  {['Description','HSN','Qty','Rate','Amount'].map(h => (
                    <th key={h} style={{ textAlign:h==='Amount'||h==='Rate'?'right':'left', padding:'6px 8px', fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em', borderBottom:'1px solid var(--border-subtle)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? items.map((item, i) => (
                  <tr key={i}>
                    <td style={{ padding:'9px 8px', color:'var(--text-primary)', fontWeight:500 }}>{item.description || item.name || b.cylinderType}</td>
                    <td style={{ padding:'9px 8px', fontFamily:'var(--font-mono)', color:'var(--text-muted)', fontSize:11 }}>{item.hsn || '27111900'}</td>
                    <td style={{ padding:'9px 8px', color:'var(--text-secondary)' }}>{item.quantity || 1}</td>
                    <td style={{ padding:'9px 8px', textAlign:'right', fontFamily:'var(--font-mono)', fontWeight:600 }}>{formatCurrency(item.rate || item.price || amount)}</td>
                    <td style={{ padding:'9px 8px', textAlign:'right', fontFamily:'var(--font-mono)', fontWeight:600 }}>{formatCurrency(item.amount || item.total || amount)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td style={{ padding:'9px 8px', color:'var(--text-primary)', fontWeight:500 }}>{b.cylinderType || 'LPG Cylinder'}</td>
                    <td style={{ padding:'9px 8px', fontFamily:'var(--font-mono)', color:'var(--text-muted)', fontSize:11 }}>27111900</td>
                    <td style={{ padding:'9px 8px', color:'var(--text-secondary)' }}>1</td>
                    <td style={{ padding:'9px 8px', textAlign:'right', fontFamily:'var(--font-mono)', fontWeight:600 }}>{formatCurrency(amount)}</td>
                    <td style={{ padding:'9px 8px', textAlign:'right', fontFamily:'var(--font-mono)', fontWeight:600 }}>{formatCurrency(amount)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={{ borderTop:'1px solid var(--border-subtle)', padding:'12px 20px', display:'flex', justifyContent:'flex-end' }}>
            <div style={{ minWidth:220 }}>
              {[['Subtotal', formatCurrency(amount)]].map(([l,v]) => (
                <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', borderBottom:'1px solid var(--border-subtle)', fontSize:12 }}>
                  <span style={{ color:'var(--text-muted)' }}>{l}</span>
                  <span style={{ fontFamily:'var(--font-mono)', color:'var(--text-secondary)' }}>{v}</span>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'space-between', padding:'9px 0' }}>
                <span style={{ fontSize:13, fontWeight:700, color:'var(--text-primary)' }}>Total</span>
                <span style={{ fontSize:17, fontWeight:800, fontFamily:'var(--font-mono)', color:'var(--accent-green)' }}>{formatCurrency(amount)}</span>
              </div>
            </div>
          </div>

          <div style={{ background:'var(--bg-surface)', borderTop:'1px solid var(--border-subtle)', padding:'9px 20px', fontSize:11, color:'var(--text-muted)', textAlign:'center' }}>
            Computer-generated invoice — no signature required.
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ── Main Bookings Page ──────────────────────────────────── */
export default function Bookings() {
  const { user } = useAuth();
  const canAdmin = ['admin','super'].includes(user?.role);

  const [rows,           setRows]           = useState([]);
  const [total,          setTotal]          = useState(0);
  const [customers,      setCustomers]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState('');
  const [filterStatus,   setFilterStatus]   = useState('All');
  const [filterCylinder, setFilterCylinder] = useState('All');
  const [page,           setPage]           = useState(1);
  const [perPage,        setPerPage]        = useState(PER_PAGE);
  const [modal,          setModal]          = useState(null);   // 'form' | 'invoice'
  const [activeBooking,  setActiveBooking]  = useState(null);   // booking row for invoice modal
  const [invoiceLoadingId, setInvoiceLoadingId] = useState(null); // track per-row loading
  const [form, setForm] = useState({
    customerId:'', cylinderType:'14.2 kg (Regular)',
    bookingDate: new Date().toISOString().split('T')[0],
    amount:903, notes:'',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        api.getBookings({ search, status:filterStatus, limit:1000 }),
        api.getCustomers({ status:'Active', limit:200 }),
      ]);
      let bookings = b.data.bookings || [];
      if (filterCylinder !== 'All') bookings = bookings.filter(bk => bk.cylinderType === filterCylinder);
      setRows(bookings);
      setTotal(bookings.length);
      setCustomers(c.data.customers || []);
      setPage(1);
    } catch { toast.error('Failed to load bookings'); }
    finally { setLoading(false); }
  }, [search, filterStatus, filterCylinder]);

  useEffect(() => { load(); }, [load]);

  const paged      = rows.slice((page-1)*perPage, page*perPage);
  const totalPages = Math.ceil(rows.length / perPage);
  const selCust    = customers.find(c => c.id === parseInt(form.customerId));

  const save = async () => {
    if (!form.customerId || !form.bookingDate) return toast.error('Select a customer and date');
    setSaving(true);
    try {
      await api.createBooking(form);
      toast.success('Booking created!');
      setModal(null);
      setForm({ customerId:'', cylinderType:'14.2 kg (Regular)', bookingDate:new Date().toISOString().split('T')[0], amount:903, notes:'' });
      load();
    } catch(err) { toast.error(err.response?.data?.message || 'Booking failed'); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try { await api.updateBookingStatus(id, status); toast.success(`Marked as ${status}`); load(); }
    catch(err) { toast.error(err.response?.data?.message || 'Update failed'); }
  };

  const remove = async id => {
    if (!window.confirm('Delete this booking?')) return;
    try { await api.deleteBooking(id); toast.success('Booking deleted'); load(); }
    catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  // Quick download — no modal
  const quickDownload = async (b) => {
    setInvoiceLoadingId(b.id);
    try {
      await downloadInvoice(b.id, b.bookingNo);
      toast.success(`Invoice ${b.bookingNo} downloaded!`);
    } catch(e) {
      toast.error(e.message);
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  // Open preview modal
  const openInvoice = (b) => {
    setActiveBooking(b);
    setModal('invoice');
  };

  const F = (k, v) => setForm(f => ({ ...f, [k]:v }));

  const activeFilters = [
    ...(filterStatus   !== 'All' ? [{ label:`Status: ${filterStatus}`,                        key:'status'   }] : []),
    ...(filterCylinder !== 'All' ? [{ label:`Cylinder: ${filterCylinder.split(' ')[0]}`,       key:'cylinder' }] : []),
  ];

  return (
    <div>
      <PageHeader
        title="Bookings"
        subtitle={`${total} total bookings`}
        action={canAdmin && <Btn onClick={() => setModal('form')} icon="＋">New Booking</Btn>}
      />

      <FilterBar actions={<ResultCount count={rows.length} total={total} label="booking" />}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search booking, customer..." />
        <FilterSelect value={filterStatus} onChange={setFilterStatus} options={STATUS_OPTS} />
        <FilterSelect
          value={filterCylinder}
          onChange={setFilterCylinder}
          options={[{ value:'All', label:'All Cylinders' }, ...CYLINDERS.map(c => ({ value:c, label:c.split(' ')[0]+' '+c.split(' ')[1] }))]}
          style={{ width:170 }}
        />
      </FilterBar>

      <ActiveFilters
        filters={activeFilters}
        onRemove={f => { if(f.key==='status') setFilterStatus('All'); else setFilterCylinder('All'); }}
      />

      <Card noPad className="au d2">
        <DataTable
          loading={loading}
          empty={rows.length === 0 ? 'No bookings found' : null}
          headers={['Booking No','Customer','Cylinder','Date','Amount','Status','Actions']}
        >
          {paged.map(b => {
            const sc = SC[b.status] || SC.Pending;
            const isDownloading = invoiceLoadingId === b.id;

            return (
              <Tr key={b.id}>
                {/* Booking No */}
                <Td>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:12, fontWeight:600, color:'var(--brand)' }}>{b.bookingNo}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>by {b.creator?.name || '—'}</div>
                </Td>

                {/* Customer */}
                <Td>
                  <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                    <Avatar name={b.customer?.name} size={28} />
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, lineHeight:1.3 }}>{b.customer?.name}</div>
                      <div style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>{b.customer?.connectionNo}</div>
                    </div>
                  </div>
                </Td>

                {/* Cylinder */}
                <Td><Badge type="gray">{b.cylinderType}</Badge></Td>

                {/* Date */}
                <Td style={{ fontSize:12.5, color:'var(--text-secondary)', whiteSpace:'nowrap' }}>{b.bookingDate}</Td>

                {/* Amount */}
                <Td style={{ fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-mono)', fontSize:13 }}>
                  ₹{parseFloat(b.amount).toLocaleString('en-IN')}
                </Td>

                {/* Status */}
                <Td><Badge type={sc.type} dot>{sc.label}</Badge></Td>

                {/* Actions */}
                <Td>
                  <div style={{ display:'flex', gap:5, flexWrap:'wrap', alignItems:'center' }}>
                    {/* Status transitions */}
                    {canAdmin && b.status === 'Pending' && (
                      <Btn size="xs" variant="info" onClick={() => updateStatus(b.id, 'Out for Delivery')}>Dispatch</Btn>
                    )}
                    {canAdmin && b.status === 'Out for Delivery' && (
                      <Btn size="xs" variant="success" onClick={() => updateStatus(b.id, 'Delivered')}>✓ Delivered</Btn>
                    )}

                    {/* ── Invoice buttons — always visible ── */}
                    <div style={{ display:'flex', gap:4 }}>
                      {/* Preview invoice */}
                      <button
                        onClick={() => openInvoice(b)}
                        title="Preview Invoice"
                        style={{
                          display:'inline-flex', alignItems:'center', gap:4,
                          padding:'3px 8px', borderRadius:'var(--radius-sm)',
                          background:'var(--accent-blue-dim)', border:'1px solid rgba(59,130,246,.2)',
                          color:'var(--accent-blue)', fontSize:11, fontWeight:600,
                          cursor:'pointer', transition:'var(--transition)', whiteSpace:'nowrap',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,.22)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'var(--accent-blue-dim)'}
                      >
                        👁 Invoice
                      </button>

                      {/* Quick download */}
                      <button
                        onClick={() => quickDownload(b)}
                        disabled={isDownloading}
                        title="Download Invoice PDF"
                        style={{
                          display:'inline-flex', alignItems:'center', gap:4,
                          padding:'3px 8px', borderRadius:'var(--radius-sm)',
                          background: isDownloading ? 'var(--bg-elevated)' : 'var(--brand-dim)',
                          border:'1px solid rgba(249,115,22,.2)',
                          color: isDownloading ? 'var(--text-muted)' : 'var(--brand)',
                          fontSize:11, fontWeight:600,
                          cursor: isDownloading ? 'not-allowed' : 'pointer',
                          transition:'var(--transition)', whiteSpace:'nowrap',
                        }}
                        onMouseEnter={e => { if(!isDownloading) e.currentTarget.style.background = 'rgba(249,115,22,.2)'; }}
                        onMouseLeave={e => { if(!isDownloading) e.currentTarget.style.background = 'var(--brand-dim)'; }}
                      >
                        {isDownloading ? '…' : '⬇ PDF'}
                      </button>
                    </div>

                    {/* Delivery date for delivered bookings */}
                    {b.status === 'Delivered' && b.deliveryDate && (
                      <span style={{ fontSize:11, color:'var(--accent-green)', fontWeight:600, whiteSpace:'nowrap' }}>
                        ✅ {b.deliveryDate}
                      </span>
                    )}

                    {/* Delete */}
                    {canAdmin && b.status !== 'Delivered' && b.status !== 'Cancelled' && (
                      <Btn size="xs" variant="danger" onClick={() => remove(b.id)}>✕</Btn>
                    )}
                  </div>
                </Td>
              </Tr>
            );
          })}
        </DataTable>

        <Paginator
          page={page} totalPages={totalPages}
          totalItems={rows.length} perPage={perPage}
          onPage={setPage} onPerPage={p => { setPerPage(p); setPage(1); }}
        />
      </Card>

      {/* ── Invoice preview modal ── */}
      {modal === 'invoice' && activeBooking && (
        <InvoiceModal
          booking={activeBooking}
          onClose={() => { setModal(null); setActiveBooking(null); }}
        />
      )}

      {/* ── New Booking modal ── */}
      {modal === 'form' && (
        <Modal title="New Gas Booking" subtitle="Create a new cylinder booking for a customer" onClose={() => setModal(null)}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <FormGroup label="Select Customer" required>
              <select value={form.customerId} onChange={e => {
                const c = customers.find(c => c.id === parseInt(e.target.value));
                if (c) setForm(f => ({ ...f, customerId:e.target.value, cylinderType:c.cylinderType, amount:PRICES[c.cylinderType]||903 }));
                else F('customerId', e.target.value);
              }} className="form-select">
                <option value="">— Select active customer —</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.connectionNo})</option>)}
              </select>
            </FormGroup>

            {selCust && (
              <div style={{ background:'var(--bg-elevated)', border:'1px solid var(--brand-glow)', borderRadius:'var(--radius-md)', padding:'10px 14px', display:'flex', gap:16, flexWrap:'wrap', fontSize:12, color:'var(--text-muted)' }}>
                <span>📞 {selCust.phone}</span>
                <span>📍 {selCust.address}</span>
                <span>🛢 {selCust.cylinderType}</span>
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <FormGroup label="Cylinder Type">
                <select value={form.cylinderType} onChange={e => setForm(f => ({ ...f, cylinderType:e.target.value, amount:PRICES[e.target.value]||f.amount }))} className="form-select">
                  {CYLINDERS.map(t => <option key={t}>{t}</option>)}
                </select>
              </FormGroup>
              <FormGroup label="Booking Date" required>
                <input type="date" value={form.bookingDate} onChange={e => F('bookingDate', e.target.value)} className="form-input" />
              </FormGroup>
              <FormGroup label="Amount (₹)">
                <input type="number" value={form.amount} onChange={e => F('amount', e.target.value)} className="form-input" />
              </FormGroup>
              <div />
              <div style={{ gridColumn:'1/-1' }}>
                <FormGroup label="Notes (optional)">
                  <textarea value={form.notes} onChange={e => F('notes', e.target.value)} placeholder="Delivery instructions, special notes..." className="form-textarea" />
                </FormGroup>
              </div>
            </div>

            <div style={{ display:'flex', gap:10, marginTop:8, justifyContent:'flex-end' }}>
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
              <Btn onClick={save} disabled={saving || !form.customerId}>{saving ? 'Creating…' : 'Confirm Booking'}</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
